import React, { useState } from 'react';
import { Button } from './ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Trash2, X, Users, Tag, Edit3 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Generic bulk action bar shown when rows are selected.
 * Props:
 *  - selectedIds: Array<string>
 *  - onClear: () => void
 *  - actions: Array<{ id, label, icon, dialogFields: [{name,label,type,options}] }>
 *  - onApply: async ({actionId, payload}) => void  (payload e.g. { commercial_id: '..' } or null for delete)
 *  - onDelete: async () => void
 *  - entityLabel: string  // e.g. "prospect(s)" or "projet(s)"
 */
export const BulkActionBar = ({ selectedIds, onClear, actions = [], onApply, onDelete, entityLabel = 'élément(s)' }) => {
    const [activeAction, setActiveAction] = useState(null);
    const [payload, setPayload] = useState({});
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    if (!selectedIds || selectedIds.length === 0) return null;

    const handleConfirm = async () => {
        if (!activeAction) return;
        setBusy(true);
        try {
            await onApply({ actionId: activeAction.id, payload });
            toast.success(`${selectedIds.length} ${entityLabel} mis à jour`);
            setActiveAction(null);
            setPayload({});
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        setBusy(true);
        try {
            await onDelete();
            toast.success(`${selectedIds.length} ${entityLabel} supprimé(s)`);
            setDeleteOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div
                className="sticky top-16 z-20 bg-[#0A0A0A] text-white py-3 px-4 rounded-sm flex items-center gap-3 flex-wrap shadow-lg"
                data-testid="bulk-action-bar"
            >
                <span className="font-semibold text-sm">
                    {selectedIds.length} {entityLabel} sélectionné(s)
                </span>
                <div className="flex-1" />
                {actions.map(a => (
                    <Button
                        key={a.id}
                        size="sm"
                        variant="secondary"
                        onClick={() => { setActiveAction(a); setPayload({}); }}
                        className="rounded-sm bg-white/10 hover:bg-white/20 text-white border-0"
                        data-testid={`bulk-action-${a.id}`}
                    >
                        {a.icon} {a.label}
                    </Button>
                ))}
                {onDelete && (
                    <Button
                        size="sm"
                        onClick={() => setDeleteOpen(true)}
                        className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
                        data-testid="bulk-action-delete"
                    >
                        <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                    </Button>
                )}
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={onClear}
                    className="text-white hover:bg-white/10"
                    data-testid="bulk-action-clear"
                >
                    <X className="w-4 h-4" />
                </Button>
            </div>

            {/* Dynamic field dialog */}
            <Dialog open={!!activeAction} onOpenChange={(o) => !o && setActiveAction(null)}>
                <DialogContent data-testid="bulk-action-dialog">
                    <DialogHeader>
                        <DialogTitle>{activeAction?.label}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        {activeAction?.dialogFields?.map(f => (
                            <div key={f.name}>
                                <Label className="field-label">{f.label}</Label>
                                <Select
                                    value={payload[f.name] || ''}
                                    onValueChange={(v) => setPayload({ ...payload, [f.name]: v })}
                                >
                                    <SelectTrigger className="h-10 rounded-sm mt-1" data-testid={`bulk-field-${f.name}`}>
                                        <SelectValue placeholder="Sélectionner..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {f.options?.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ))}
                        <p className="text-sm text-[#52525B]">
                            Cette action s'appliquera à <b>{selectedIds.length}</b> {entityLabel}.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActiveAction(null)}>Annuler</Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={busy || activeAction?.dialogFields?.some(f => f.required && !payload[f.name])}
                            className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white"
                            data-testid="bulk-action-confirm"
                        >
                            {busy ? 'Application...' : 'Appliquer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent data-testid="bulk-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Confirmer la suppression</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-[#52525B]">
                        Vous êtes sur le point de supprimer <b>{selectedIds.length}</b> {entityLabel}.
                        Cette action est irréversible et supprime aussi tous les éléments liés (dossiers, documents, commentaires...).
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteOpen(false)}>Annuler</Button>
                        <Button
                            onClick={handleDelete}
                            disabled={busy}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="bulk-delete-confirm"
                        >
                            {busy ? 'Suppression...' : 'Supprimer définitivement'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

// Small checkbox used in tables
export const SelectCheckbox = ({ checked, onChange, dataTestid }) => (
    <input
        type="checkbox"
        checked={checked}
        onChange={(e) => { e.stopPropagation(); onChange(e.target.checked); }}
        onClick={(e) => e.stopPropagation()}
        className="w-4 h-4 rounded-sm accent-[#E85A24] cursor-pointer"
        data-testid={dataTestid}
    />
);
